/**
 * Centralized API Client for DM Assistant
 *
 * This is the single source of truth for all API calls in the application.
 * It handles authentication, token refresh, and error classification.
 *
 * Key Features:
 * - Automatic Bearer token injection for protected routes
 * - Automatic token refresh on 401 responses
 * - Error classification into specific error types (AuthenticationError, NotFoundError, etc.)
 * - Credentials mode: 'omit' for most requests, 'include' only for /api/auth/refresh
 *
 * Security Note:
 * Bearer tokens sent via Authorization header provide inherent CSRF protection because
 * they are not automatically attached by the browser (unlike cookies). This makes the
 * application immune to CSRF attacks by design (ADR-032).
 *
 * Architecture Decisions:
 * - ADR-031: Phase 1 Foundation Stabilization
 * - ADR-032: Pure Bearer Token Authentication (CSRF removal, credentials configuration)
 * - ADR-037: API Client Consolidation (single api.ts, removed apiService.ts)
 *
 * @see docs/adr/plans/implementation/PHASE-1-COMPLETION-REPORT.md
 * @see docs/adr/0031-comprehensive-architectural-refactoring.md
 * @see docs/adr/0032-pure-bearer-token-authentication.md
 */

import { useGlobalState } from '../state/useGlobalState';
import { logger } from '../services/logger.client';
import { ApiError, AuthenticationError, NotFoundError, ServerError, SessionExpiredError } from './apiErrors';

const PUBLIC_ROUTES = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/global-cache',
];

const REFRESH_TIMEOUT = 10000; // 10 seconds

let isRefreshing = false;

// A queue of promises waiting for a new token.
// Each entry contains both resolve and reject functions for proper error handling.
interface TokenSubscriber {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}
let tokenSubscribers: TokenSubscriber[] = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  (tokenSubscribers ?? []).forEach(subscriber => {
    if (error) {
      // Properly reject queued promises to prevent memory leaks and hanging requests.
      // This allows the calling code to handle the error appropriately.
      subscriber.reject(error);
    } else if (token) {
      subscriber.resolve(token);
    }
  });
  tokenSubscribers = [];
};

/**
 * Parses an error response and throws an appropriate custom error.
 * @param response The failed fetch response.
 */
async function handleErrorResponse(response: Response) {
  let errorPayload: unknown;
  try {
    errorPayload = await response.json();
  } catch {
    errorPayload = { message: response.statusText || 'An unknown error occurred.' };
  }

  switch (response.status) {
    case 401:
      throw new AuthenticationError(errorPayload);
    case 404:
      throw new NotFoundError(errorPayload);
    case 500:
      throw new ServerError(errorPayload);
    default:
      throw new ApiError((errorPayload as { message: string }).message, response.status, errorPayload);
  }
}

async function performFetch(url: string, options: RequestInit): Promise<Response> {
  const token = useGlobalState.getState().auth.token;
  const headers = new Headers(options.headers ?? {});
  // Do not set Content-Type for FormData, the browser does it with the correct boundary,
  // and it should not be application/json.
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (token && !PUBLIC_ROUTES.includes(url)) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Note: X-CSRF-Token header removed per ADR-032. Bearer tokens in the Authorization
  // header provide inherent CSRF protection since browsers do not auto-attach custom headers.

  // Determine credentials mode per ADR-032 (Pure Bearer Token Authentication):
  // - 'include' for /api/auth/refresh: needs httpOnly cookie containing refresh token
  // - 'omit' for all other endpoints: Bearer token is sent in Authorization header
  // SECURITY: Use exact URL matching to prevent URLs like '/api/auth/refresh-status'
  // or '/api/callback?redirect=/api/auth/refresh' from incorrectly receiving credentials.
  const isRefreshEndpoint = url === '/api/auth/refresh' || url.startsWith('/api/auth/refresh?');
  const credentials = isRefreshEndpoint ? 'include' : 'omit';

  return fetch(url, { ...options, headers, credentials });
}

async function refreshTokenAndRetry(url: string, options: RequestInit): Promise<Response> {
  logger.error({ url, timestamp: new Date().toISOString() }, '[API-DEBUG] refreshTokenAndRetry: 401 received, attempting refresh');
  if (isRefreshing) {
    // If a refresh is already in progress, queue this request.
    return new Promise<Response>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        // Remove this subscriber from the queue to prevent memory leak on timeout.
        const index = tokenSubscribers.findIndex(s => s.resolve === onSuccess);
        if (index !== -1) {
          tokenSubscribers.splice(index, 1);
        }
        reject(new Error(`Token refresh timed out after ${REFRESH_TIMEOUT / 1000} seconds.`));
      }, REFRESH_TIMEOUT);

      const onSuccess = (_token: string) => {
        clearTimeout(timeoutId);
        // Once we have the new token, we don't retry here. We resolve the promise,
        // and the original `api` function's loop will perform the retry.
        resolve(performFetch(url, options));
      };

      const onFailure = (error: Error) => {
        clearTimeout(timeoutId);
        reject(error);
      };

      tokenSubscribers.push({ resolve: onSuccess, reject: onFailure });
    });
  }

  isRefreshing = true;
  try {
    // Must include credentials to send the httpOnly refresh token cookie (ADR-032)
    logger.error({ timestamp: new Date().toISOString() }, '[API-DEBUG] Calling POST /api/auth/refresh');
    const refreshResponse = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
    logger.error({ status: refreshResponse.status, ok: refreshResponse.ok, timestamp: new Date().toISOString() }, '[API-DEBUG] /api/auth/refresh response');
    if (!refreshResponse.ok) {
      throw new SessionExpiredError();
    }
    const { accessToken } = await refreshResponse.json();
    useGlobalState.getState().auth.setToken(accessToken);
    processQueue(null, accessToken);
    const retryResponse = await performFetch(url, options);
    return retryResponse;
  } catch (error) {
    const errorToProcess = error instanceof Error ? error : new Error(String(error));
    processQueue(errorToProcess, null);
    logger.error({ err: error, timestamp: new Date().toISOString() }, '[API-DEBUG] Token refresh failed. Logging out.');
    useGlobalState.getState().auth.logout(); // No need to await this
    throw error instanceof ApiError ? error : new SessionExpiredError();
  } finally {
    isRefreshing = false;
  }
}

export const api = async (url:string, options: RequestInit = {}): Promise<Response> => {
  let response = await performFetch(url, options);

  if (response.status === 401 && !PUBLIC_ROUTES.includes(url) && useGlobalState.getState().auth.token) {
    response = await refreshTokenAndRetry(url, options);
  }

  // Centralized error handling for non-401 errors
  if (!response.ok) {
    await handleErrorResponse(response);
  }

  return response;
};

/**
 * Reset the module's internal state. This is only intended for use in tests
 * to ensure proper isolation between test cases.
 *
 * Clears the token refresh queue and resets the refresh-in-progress flag.
 * Note: CSRF token handling was removed per ADR-032 (Pure Bearer Token Authentication).
 *
 * @internal - Do not use in production code
 */
export const _resetApiModuleState = () => {
  isRefreshing = false;
  tokenSubscribers = [];
};